"use client";

import {
  ColumnFiltersState,
  ColumnPinningState,
  FilterFn,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useRef, useState, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { AlertTriangle } from "lucide-react";
import { Table, TableBody, TableHeader, TableRow } from "@/components/ui/table";
import { Loading } from "@/components/Loading";
import THead from "./THead";
import TCell from "./TCell";
import BulkActions from "./BulkActions";
import { Pagination } from "./Pagination";
import { CustomTableProps } from "@/lib/types/table.types";
import useTableStore from "@/lib/stores/tableStore";
import "@/styles/globals.css";

const CustomTable = <T extends Record<string, any>>({
  tableId,
  columns,
  rows,
  defaultPageSize = 10,
  manualPagination = false,
  customPagination = false,
  defaultPinnedColumns,
  cardComponent,
  bulkActions,
  manualSearch,
  rightTop,
  leftTop,
  viewMode = "table",
  pageOffset = 15.5,
  scrollable = true,
  isLoading = false,
  defaultSorting,
  maxHeight,
  minHeight,
  emptyContent,
}: CustomTableProps<T>) => {
  const { setTableData, tableData } = useTableStore();

  const initialPagination = useMemo(() => {
    return (
      tableData.find((t) => t.tableId === tableId)?.pagination || {
        pageIndex: 0,
        pageSize: defaultPageSize,
      }
    );
  }, [tableId, tableData, defaultPageSize]);

  const [pagination, setPagination] =
    useState<PaginationState>(initialPagination);

  const initialRowsData = useMemo(
    () => ({
      ...rows,
      data: rows.data || [],
      rowCount: rows.rowCount || rows.data?.length || 0,
      pageCount:
        rows.pageCount ||
        Math.ceil((rows.data?.length || 0) / initialPagination.pageSize),
    }),
    [rows, initialPagination.pageSize]
  );

  const [rowsData, setRowsData] =
    useState<CustomTableProps<T>["rows"]>(initialRowsData);

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    defaultPinnedColumns
      ? {
          left: ["select", ...(defaultPinnedColumns?.left || [])],
          right: [...(defaultPinnedColumns?.right || [])],
        }
      : {
          left: [],
          right: [],
        }
  );
  const [sorting, setSorting] = useState<SortingState>(
    tableData.find((t) => t.tableId === tableId)?.sorting ||
      defaultSorting ||
      []
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    tableData.find((t) => t.tableId === tableId)?.columnFilters || []
  );
  const [globalFilter, setGlobalFilter] = useState<any>(
    tableData.find((t) => t.tableId === tableId)?.globalFilter || ""
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    tableData.find((t) => t.tableId === tableId)?.rowSelection || {}
  );
  const weakIncludesStringFn: FilterFn<T> = (
    srows: Row<T>,
    columnId: string,
    filterValue: any
  ) => {
    const rowValue = srows.getValue(columnId);

    // Eğer rowValue null veya undefined ise eşleşme yok
    if (rowValue == null) return false;

    // String olarak karşılaştırma yap
    const rowValueString = String(rowValue).toLowerCase();
    const filterValueString = String(filterValue).toLowerCase();

    // Case-insensitive içerik eşleşmesi veya zayıf eşitlik
    return (
      rowValueString.includes(filterValueString) || rowValue === filterValue
    );
  };
  useEffect(() => {
    if (!rows.data) return;

    const newRowCount = rows.rowCount || rows.data.length;
    const newPageCount =
      rows.pageCount || Math.ceil(newRowCount / pagination.pageSize);

    setRowsData({
      ...rows,
      data: rows.data,
      rowCount: newRowCount,
      pageCount: newPageCount,
    });
  }, [rows, pagination.pageSize]);

  useEffect(() => {
    setTableData({
      tableId,
      columnFilters,
      columnPinning,
      sorting,
      pagination,
      globalFilter,
      rowSelection,
    });
  }, [
    tableId,
    columnFilters,
    columnPinning,
    sorting,
    pagination,
    globalFilter,
    rowSelection,
    setTableData,
  ]);
  console.log(columnFilters);
  const table = useReactTable<T>({
    columns,
    data: rowsData.data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    state: {
      columnFilters,
      columnPinning,
      sorting,
      pagination,
      globalFilter,
      rowSelection,
    },
    rowCount: rowsData.rowCount,
    onColumnPinningChange: setColumnPinning,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    globalFilterFn: weakIncludesStringFn,
    manualPagination,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 100,
      size: 100,
    },
    pageCount: rowsData.pageCount,
    manualFiltering: false,
  });

  const tableContainerRef = useRef<any>(null);
  const rowVirtualizer = useVirtualizer({
    count: Math.max(
      pagination.pageSize < table.getFilteredRowModel().rows.length
        ? pagination.pageSize
        : table.getFilteredRowModel().rows.length,
      0
    ),
    estimateSize: () => 300,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });
  const selectedRows = rowsData.data.filter((_, index) => rowSelection[index]);
  const actions = {
    selectedRows,
    pagination,
    setRowSelection,
    setGlobalFilter,
    setPagination,
    table,
  };
  const renderBulkActions = () => {
    if (typeof bulkActions === "function") {
      return bulkActions(actions);
    }
    return bulkActions;
  };

  const renderCardComponent = (row: Row<T>) => {
    if (typeof cardComponent === "function") {
      return cardComponent({ row });
    }
    return cardComponent;
  };

  const renderRightTop = () => {
    if (typeof rightTop === "function") {
      return rightTop(actions);
    }
    return rightTop;
  };

  const renderLeftTop = () => {
    if (typeof leftTop === "function") {
      return leftTop(actions);
    }
    return leftTop;
  };

  const renderCustomPagination = () => {
    if (typeof customPagination === "function") {
      return customPagination(actions);
    }
    return customPagination;
  };
  const { rows: rowsModel } = table.getPaginationRowModel();
  useEffect(() => {
    if (!manualPagination) {
      const filteredRowCount =
        table.getState().columnFilters.length === 0
          ? rows.data.length
          : table.getFilteredRowModel().rows.length;
      const newPageCount = Math.ceil(filteredRowCount / pagination.pageSize);
      setRowsData((prev) => {
        return {
          ...prev,
          rowCount: filteredRowCount,
          pageCount: newPageCount,
        };
      });
      if (pagination.pageIndex >= newPageCount) {
        setPagination((prev) => ({
          ...prev,
          pageIndex: 0,
        }));
      }
    } else {
      setRowsData(rows);
    }
  }, [
    columnFilters,
    globalFilter,
    pagination.pageSize,
    table,
    pagination.pageIndex,
    manualPagination,
    rows,
  ]);

  return (
    <div className="w-full">
      <div className="rounded-2.5xl max-w-full grid relative h-full">
        {bulkActions && <BulkActions>{renderBulkActions()}</BulkActions>}
        <div className="md:p-3 flex flex-col gap-3 transition-none overflow-auto relative">
          {isLoading && <Loading />}
          <div
            className={`flex justify-between items-center ${
              !bulkActions && !manualSearch && !manualPagination ? "p-3" : ""
            }`}
          >
            <div className="flex items-center h-full flex-1">
              {!manualSearch && (
                <input
                  className="h-10 w-full max-w-72 rounded-lg px-3 border border-gray-300"
                  value={globalFilter}
                  onChange={(e) =>
                    table.setGlobalFilter(String(e.target.value))
                  }
                  placeholder="Ara"
                />
              )}
              {leftTop && (
                <div className="flex-1 flex justify-start items-center gap-2">
                  {renderLeftTop()}
                </div>
              )}
            </div>
            {rightTop && (
              <div className="flex-1 flex justify-end items-center gap-2">
                {renderRightTop()}
              </div>
            )}
          </div>
          <div
            ref={tableContainerRef}
            style={{
              minHeight: scrollable ? "70%" : minHeight || "auto",
              maxHeight: scrollable
                ? `calc(100svh - ${pageOffset}rem)`
                : maxHeight &&
                  Number(maxHeight) > rowVirtualizer.getTotalSize() + 48
                ? maxHeight
                : `calc(${rowVirtualizer.getTotalSize()}px + 64px)`,
            }}
            className="overflow-auto max-sm:flex h-screen"
          >
            {viewMode === "table" && (
              <Table style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow isSticky key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <THead key={header.id} table={table} header={header} />
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                {!isLoading &&
                  table.getFilteredRowModel().rows.length === 0 && (
                    <>
                      {emptyContent ? (
                        emptyContent
                      ) : (
                        <div className="w-full bg-yellow-100 text-yellow-800 flex items-center p-2 gap-2">
                          <span className="flex items-center gap-2 sticky left-2">
                            <AlertTriangle />
                            Sonuç bulunamadı
                          </span>
                        </div>
                      )}
                    </>
                  )}

                {!isLoading && table.getFilteredRowModel().rows.length > 0 && (
                  <TableBody className="grid relative mb-auto">
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const row = rowsModel[virtualRow.index] as Row<T>;
                      if (!row) return null;
                      return (
                        <TableRow
                          data-index={virtualRow.index}
                          ref={(node) => rowVirtualizer.measureElement(node)}
                          key={row.id}
                          className="flex absolute w-full"
                          style={{
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          {row.getVisibleCells().map((cell) => {
                            const header = table
                              .getHeaderGroups()
                              .flatMap((headerGroup) => headerGroup.headers)
                              .find((h) => h.id === cell.column.id);
                            return (
                              <TCell
                                key={cell.id}
                                table={table}
                                cell={cell}
                                header={header as Header<T, unknown>}
                              />
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}
              </Table>
            )}
          </div>
          {viewMode === "card" && (
            <div className="h-full absolute top-[90px] left-0 p-3 w-full">
              <div className="grid grid-cols-2 gap-5">
                {table
                  .getPaginationRowModel()
                  .rows.map((row) => renderCardComponent(row))}
              </div>
            </div>
          )}
        </div>
        {!manualPagination && !customPagination && table && (
          <Pagination
            rows={rowsData}
            table={table}
            pagination={pagination}
            setPagination={setPagination}
          />
        )}
        {manualPagination && customPagination && renderCustomPagination()}
      </div>
    </div>
  );
};

export { CustomTable };
